import React from 'react';
import { Box, Typography } from '@mui/material';
import MoveCard from './MoveCard';
import FloorPlan from './FloorPlan';
import RackPanel from './RackPanel';
import { VetoControls } from './exclusions';
import { Eyebrow, StatStrip, ZoneNumeral } from './Badges';
import {
  asksSummary, capacitySourceLabel, countedMoves, monthDay, pieces, walkFixtureIds,
} from './format';
import { C, MONO, RADIUS, SHADOW } from './tokens';
import {
  RestockV2ExcludedStyle, RestockV2Fixture, RestockV2ListExcludedVariant, RestockV2Move,
  RestockV2Plan, RestockV2ThumbTarget,
} from './types';

interface FloorMapProps {
  plan: RestockV2Plan;
  moves: RestockV2Move[];
  doneRows: Record<string, boolean>;
  pendingRows: Record<string, boolean>;
  whyRows: Record<string, boolean>;
  thumbIndex: Record<string, RestockV2ThumbTarget>;
  // ≥1024 px: the plan on the left, the selected rack's whole panel on the right.
  desktop: boolean;
  busy: boolean;
  selectedId: string | null;
  onZone: (fixtureId: string) => void;
  onWalk: () => void;
  onAdopt: () => void;
  onDone: (move: RestockV2Move) => void;
  onUndo: (move: RestockV2Move) => void;
  onSkip: (move: RestockV2Move) => void;
  skipBusy: (move: RestockV2Move) => boolean;
  veto: VetoControls;
  excludedStyles: RestockV2ExcludedStyle[];
  keptOffRail?: RestockV2ListExcludedVariant[];
  onUnkeepRow?: (row: RestockV2ListExcludedVariant) => void;
  onAddBack: (sku: string) => void;
  onWhy: (id: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const FirstAction: React.FC<{
  move: RestockV2Move; done: boolean; pending: boolean; whyOpen: boolean; arrival: string;
  skipPending: boolean;
  onDone: (move: RestockV2Move) => void; onUndo: (move: RestockV2Move) => void;
  onSkip: (move: RestockV2Move) => void;
  onWhy: (id: string) => void; onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ move, done, pending, whyOpen, arrival, skipPending, onDone, onUndo, onSkip, onWhy, onImage }) => (
  <Box
    sx={{
      border: '1px solid', borderColor: C.line, bgcolor: C.card, borderRadius: `${RADIUS}px`,
      boxShadow: SHADOW, overflow: 'hidden',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.4, py: 1.1, bgcolor: C.ink, color: C.onInk }}>
      <Typography component="h3" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '.02em' }}>先做這一件</Typography>
      <Typography sx={{ ml: 'auto', fontSize: 12, fontFamily: MONO, color: C.onInk2 }}>{move.fixtureLabel}</Typography>
    </Box>
    <Box sx={{ p: 1 }}>
      <MoveCard
        move={move} done={done} pending={pending} whyOpen={whyOpen} arrivalLabel={arrival}
        skipPending={skipPending}
        onDone={onDone} onUndo={onUndo} onSkip={onSkip} onWhy={onWhy} onImage={onImage}
      />
    </Box>
  </Box>
);

// 衣架地圖 — the counter as a map. Phone: plan, then the first action, then 開始走一圈.
// Desktop: the plan sticky on the left and the selected rack's full panel on the right, because on a
// PC there is no reason to hide the rack behind a tap.
const FloorMap: React.FC<FloorMapProps> = ({
  plan, moves, doneRows, pendingRows, whyRows, thumbIndex, desktop, busy, selectedId,
  onZone, onWalk, onAdopt, onDone, onUndo, onSkip, skipBusy, veto,
  excludedStyles, keptOffRail, onUnkeepRow, onAddBack, onWhy, onImage,
}) => {
  const meta = plan.meta;
  const fit = meta?.fit;
  const walk = walkFixtureIds(plan);
  const todo = countedMoves(moves);
  const open = todo.filter((move) => !doneRows[move.id]);
  const first = open[0] || todo[0] || null;
  const autumn = meta?.autumn;
  const asks = asksSummary(autumn?.asks);
  const arrival = monthDay(meta?.expected_arrival);
  const capacityNote = capacitySourceLabel(meta?.capacity_source);
  const selected: RestockV2Fixture | null = (plan.racks?.fixtures || [])
    .filter((fixture) => fixture.id === selectedId)[0] || null;

  const firstAction = first && (
    <FirstAction
      move={first} done={!!doneRows[first.id]} pending={!!pendingRows[first.id]}
      whyOpen={!!whyRows[first.id]} arrival={arrival} skipPending={skipBusy(first)}
      onDone={onDone} onUndo={onUndo} onSkip={onSkip} onWhy={onWhy} onImage={onImage}
    />
  );

  const notes = (
    <>
      <Eyebrow>今天這樣算的</Eyebrow>
      <Box sx={{ fontSize: 13, color: C.ink2, lineHeight: 1.6 }}>
        <Box component="div">
          · 下次下單 {monthDay(meta?.next_order_night)} 晚 · 約 {arrival} 到店 · 進貨 {fit?.incoming_garments ?? 0} 件
        </Box>
        <Box component="div">· 今天要動 {pieces(todo.map((move) => move.line))} 件衣服，分 {todo.length} 件事</Box>
        {(meta?.data_notes || []).map((note) => <Box key={note} component="div">· {note}</Box>)}
      </Box>
    </>
  );

  return (
    <Box>
      <StatStrip
        cells={[
          { label: '現在架上', value: fit?.rail_now ?? meta?.rail_now ?? 0, suffix: '件' },
          {
            label: '做完之後',
            value: fit?.end_state ?? meta?.rail_now ?? 0,
            suffix: `／${fit?.capacity ?? meta?.capacity ?? 0}`,
            highlight: (fit?.still_over_by ?? 0) > 0,
          },
          { label: '今天要動', value: todo.length, suffix: '件事', highlight: true },
        ]}
      />
      {capacityNote && (
        <Typography sx={{ fontSize: 12, color: C.ink3, mb: 1.5, mt: -1 }}>容量＝{capacityNote}</Typography>
      )}

      {autumn && (
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 1.5, px: 1.25, py: 1,
            bgcolor: C.waitWash, border: '1px solid', borderColor: C.wait, borderRadius: `${RADIUS}px`,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.wait }}>
            秋款 現在 {autumn.current_pct}% → 做完後 {autumn.next_pct}%（今天目標 {autumn.target_pct}%）
          </Typography>
          {asks && <Typography sx={{ fontSize: 12, color: C.ink2 }}>客人問過：{asks}</Typography>}
        </Box>
      )}

      {desktop ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 40fr) minmax(0, 60fr)', gap: 2, alignItems: 'start' }}>
          <Box sx={{ position: 'sticky', top: 96 }}>
            <FloorPlan
              plan={plan} moves={moves} thumbIndex={thumbIndex} desktop selectedId={selectedId} onZone={onZone}
            />
            {notes}
          </Box>
          <Box>
            {firstAction}
            {selected ? (
              <RackPanel
                fixture={selected} plan={plan} moves={moves} doneRows={doneRows} pendingRows={pendingRows}
                whyRows={whyRows} thumbIndex={thumbIndex} desktop busy={busy} onAdopt={onAdopt}
                onDone={onDone} onUndo={onUndo} onSkip={onSkip} skipBusy={skipBusy}
                veto={veto} excludedStyles={excludedStyles} keptOffRail={keptOffRail}
                onUnkeepRow={onUnkeepRow} onAddBack={onAddBack} onWhy={onWhy} onImage={onImage}
                header={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.25, mb: 1.25 }}>
                    <ZoneNumeral index={walk.indexOf(selected.id)} bg={C.back} size={13} />
                    <Typography component="h2" sx={{ fontSize: 19, fontWeight: 900, letterSpacing: '-.01em' }}>
                      {selected.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: C.ink3, ml: 'auto' }}>
                      點左邊的平面圖換一個架子
                    </Typography>
                  </Box>
                )}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: C.ink2, mt: 2 }}>點左邊平面圖上的一個架子。</Typography>
            )}
          </Box>
        </Box>
      ) : (
        <>
          <FloorPlan
            plan={plan} moves={moves} thumbIndex={thumbIndex} desktop={false} selectedId={null} onZone={onZone}
          />
          <Box sx={{ mt: 1.75 }}>{firstAction}</Box>
          <Box
            component="button" type="button" onClick={onWalk}
            sx={{
              display: 'block', width: '100%', mt: 1.25, py: 1.6, cursor: 'pointer',
              border: '1px dashed', borderColor: C.line2, bgcolor: C.wash, borderRadius: `${RADIUS}px`,
              fontWeight: 800, fontSize: 14, color: C.ink,
            }}
          >
            開始走一圈 →
          </Box>
          {notes}
        </>
      )}
    </Box>
  );
};

export default FloorMap;
